import * as Sentry from '@sentry/browser';
import { saveAs } from 'file-saver';
import { zipSync } from 'fflate';
import { compressionLevel } from './compression-levels.js';
import { createSemaphore, fetchWithRetry } from './async-utils.js';

const CHUNK_BYTES = 200 * 1_000_000; // 200MB raw — ZIP blob will always be ≤ this
const FETCH_CONCURRENCY = 5;

// fetchWithRetry throws `HTTP ${status} for ${url}` on non-OK responses — strip the
// presigned URL before logging so it never reaches Sentry. For other errors (network
// timeouts, etc.) the message contains no URL and passes through unchanged.
function sanitizeErrMessage(msg) {
	const httpMatch = /\bHTTP (\d{3})\b/.exec(msg || '');
	if (httpMatch) return `HTTP ${httpMatch[1]}`;
	return (msg || 'Unknown error').replace(/https?:\/\/\S+/g, '[url]');
}

// Unified per-file failure reporter: calls onProgress and sends one Sentry event.
// Skips Sentry for simulated failures so test runs do not pollute the issue stream.
// Fingerprinted by HTTP status (or 'network-error') so a mass failure on one status
// code lands in a single Sentry issue rather than N identical ones.
function reportFileFail({ fileName, errorMessage, httpStatus, onProgress, doneCount, total, extra }) {
	onProgress({ phase: 'error', fileName, error: errorMessage, done: doneCount, total });

	if (errorMessage.includes('Simulated failure')) return;

	Sentry.captureMessage('Bulk document download: file fetch failed', {
		level: 'error',
		extra: {
			fileName,
			errorMessage,
			...(httpStatus != null ? { httpStatus } : {}),
			pathname: typeof window !== 'undefined' ? window.location.pathname : undefined,
			...extra,
		},
		fingerprint: ['bulk-download-file-fail', httpStatus != null ? String(httpStatus) : 'network-error'],
	});
}

/**
 * Split file entries into chunks bounded by raw size.
 * A single file larger than maxBytes becomes its own solo chunk.
 *
 * @param {Array<{name: string, data: Uint8Array, sizeBytes: number}>} entries
 * @param {number} maxBytes
 * @returns {Array<Array>}
 */
export function chunkBySize(entries, maxBytes = CHUNK_BYTES) {
	const chunks = [];
	let current = [],
		currentSize = 0;

	for (const entry of entries) {
		if (entry.sizeBytes > maxBytes) {
			if (current.length) {
				chunks.push(current);
				current = [];
				currentSize = 0;
			}
			chunks.push([entry]);
			continue;
		}
		if (currentSize + entry.sizeBytes > maxBytes && current.length) {
			chunks.push(current);
			current = [];
			currentSize = 0;
		}
		current.push(entry);
		currentSize += entry.sizeBytes;
	}
	if (current.length) chunks.push(current);
	return chunks;
}

/**
 * Build a ZIP blob from an array of file entries using fflate.
 * PDFs, Office docs, and other already-compressed formats are stored (level 0).
 * CSV, XML, and other text formats are deflated (level 4).
 *
 * @param {Array<{name: string, data: Uint8Array}>} entries
 * @returns {Blob}
 */
export function buildZipBlob(entries) {
	const files = {};
	for (const { name, data } of entries) {
		files[name] = [data, { level: compressionLevel(name), unicode: true }];
	}
	const zipped = zipSync(files, { consume: true });
	return new Blob([zipped], { type: 'application/zip' });
}

/**
 * Download multiple files as one or more ZIP archives.
 *
 * @param {Array<{name: string, url: string, sizeBytes?: number}>} files
 *   - name: filename to use inside the ZIP (should include extension)
 *   - url: presigned URL to fetch the file from
 *   - sizeBytes: optional; used for chunk pre-assignment. If omitted, chunks are
 *     assigned after fetch using actual sizes (requires all files in memory at once —
 *     acceptable for small batches; provide sizeBytes from metadata for large batches)
 * @param {object} [options]
 * @param {string} [options.zipBaseName='Documents']  Base name for ZIP files
 * @param {function} [options.onProgress]             Called with progress updates
 *   Signature: ({ phase, done, total, part, parts, error, fileName })
 *
 * @returns {Promise<void>}
 */
export async function download(files, { zipBaseName = 'Documents', onProgress = () => {} } = {}) {
	if (!files.length) return;

	const total = files.length;
	let done = 0;
	const semaphore = createSemaphore(FETCH_CONCURRENCY);

	const sizesKnown = files.every((f) => typeof f.sizeBytes === 'number');

	if (sizesKnown) {
		// Pre-assign files to chunks from metadata — fetch each chunk serially.
		// This bounds memory to one chunk at a time regardless of total archive size.
		const chunks = chunkBySize(
			files.map((f) => ({ ...f, data: null })),
			CHUNK_BYTES
		);

		for (let i = 0; i < chunks.length; i++) {
			const chunkFiles = chunks[i];

			// Fetch this chunk's files with concurrency control
			const entries = await Promise.all(
				chunkFiles.map((f) =>
					semaphore(async () => {
						try {
							const res = await fetchWithRetry(f.url);
							if (!res.ok) {
								reportFileFail({
									fileName: f.name,
									errorMessage: `HTTP ${res.status}`,
									httpStatus: res.status,
									onProgress,
									doneCount: ++done,
									total,
									extra: { chunkPart: i + 1, chunkTotal: chunks.length },
								});
								return null;
							}
							const buffer = await res.arrayBuffer();
							const data = new Uint8Array(buffer);
							onProgress({ phase: 'downloading', done: ++done, total });
							return { name: f.name, data, sizeBytes: data.byteLength };
						} catch (err) {
							const errorMessage = sanitizeErrMessage(err.message);
							const httpStatus = /\bHTTP (\d{3})\b/.exec(err.message || '');
							reportFileFail({
								fileName: f.name,
								errorMessage,
								httpStatus: httpStatus ? parseInt(httpStatus[1], 10) : undefined,
								onProgress,
								doneCount: ++done,
								total,
								extra: { chunkPart: i + 1, chunkTotal: chunks.length },
							});
							return null;
						}
					})
				)
			);

			const valid = entries.filter(Boolean);
			if (!valid.length) continue;

			onProgress({ phase: 'compressing', part: i + 1, parts: chunks.length });
			const blob = buildZipBlob(valid);
			const suffix = chunks.length > 1 ? `_Part${i + 1}_of_${chunks.length}` : '';
			saveAs(blob, `${zipBaseName}${suffix}.zip`);

			// Yield to give GC an opportunity before next chunk
			await new Promise((r) => setTimeout(r, 400));
		}
	} else {
		// Sizes not available — fetch all files first, then chunk by actual size.
		// Acceptable for small batches (<50 files). Provide sizeBytes for large batches.
		const entries = await Promise.all(
			files.map((f) =>
				semaphore(async () => {
					try {
						const res = await fetchWithRetry(f.url);
						if (!res.ok) {
							reportFileFail({
								fileName: f.name,
								errorMessage: `HTTP ${res.status}`,
								httpStatus: res.status,
								onProgress,
								doneCount: ++done,
								total,
							});
							return null;
						}
						const buffer = await res.arrayBuffer();
						const data = new Uint8Array(buffer);
						onProgress({ phase: 'downloading', done: ++done, total });
						return { name: f.name, data, sizeBytes: data.byteLength };
					} catch (err) {
						const errorMessage = sanitizeErrMessage(err.message);
						const httpStatus = /\bHTTP (\d{3})\b/.exec(err.message || '');
						reportFileFail({
							fileName: f.name,
							errorMessage,
							httpStatus: httpStatus ? parseInt(httpStatus[1], 10) : undefined,
							onProgress,
							doneCount: ++done,
							total,
						});
						return null;
					}
				})
			)
		);

		const valid = entries.filter(Boolean);
		if (!valid.length) return;

		const chunks = chunkBySize(valid, CHUNK_BYTES);

		for (let i = 0; i < chunks.length; i++) {
			onProgress({ phase: 'compressing', part: i + 1, parts: chunks.length });
			const blob = buildZipBlob(chunks[i]);
			const suffix = chunks.length > 1 ? `_Part${i + 1}_of_${chunks.length}` : '';
			saveAs(blob, `${zipBaseName}${suffix}.zip`);
			await new Promise((r) => setTimeout(r, 400));
		}
	}
}
